import { Category } from '@/api/models';
import { EntityList } from '@/components/EntityList';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { queryKeys } from '@/lib/queryKeys';
import { mdiTag, mdiTagMultiple } from '@mdi/js';
import * as React from 'react';
import { CategoriesFilters } from './CategoriesFilters';
import { CategoriesTable } from './CategoriesTable';

export interface CategoriesListProps {
  onRowClick: (category: Category) => void;
  onEditCategory: (category: Category) => void;
  onDeleteCategory: (category: Category) => void;
  onCreateCategory: () => void;
  isDeleting: boolean;
}

export const CategoriesList: React.FC<CategoriesListProps> = ({
  onRowClick,
  onEditCategory,
  onDeleteCategory,
  onCreateCategory,
  isDeleting,
}) => {
  const listState = usePaginatedList<Category, { enabled: string }>({
    queryKey: queryKeys.ADMIN_CATEGORIES,
    endpoint: '/admin/categories',
    initialFilters: { enabled: '' },
    buildQueryParams: ({
      page,
      limit,
      search,
      sortBy,
      sortOrder,
      filters,
    }) => ({
      page,
      limit,
      search: search || undefined,
      enabled: filters.enabled ? filters.enabled === 'true' : undefined,
      sortBy: sortBy as 'name' | 'createdAt',
      sortOrder,
    }),
  });

  return (
    <EntityList
      title='Categories'
      description='Manage business categories for classification'
      icon={mdiTag}
      countIcon={mdiTagMultiple}
      countLabel={`${listState.list.total} total categories`}
      createLabel='Add Category'
      listState={listState}
      onCreateClick={onCreateCategory}
      renderFilters={() => (
        <CategoriesFilters
          searchTerm={listState.state.search}
          selectedEnabled={listState.state.filters.enabled}
          onSearchChange={listState.actions.setSearch}
          onEnabledChange={value =>
            listState.actions.setFilter('enabled', value)
          }
          onClearFilters={listState.actions.clearFilters}
        />
      )}
      renderTable={() => (
        <CategoriesTable
          categories={listState.list.items}
          isLoading={listState.isLoading}
          sortBy={listState.state.sortBy}
          sortOrder={listState.state.sortOrder}
          onSort={listState.actions.setSort}
          onRowClick={onRowClick}
          onEditCategory={onEditCategory}
          onDeleteCategory={onDeleteCategory}
          isDeleting={isDeleting}
        />
      )}
    />
  );
};
